import { cn } from '../../../lib/cn'
import type { ResumoGmud, StatusGmud } from '../../../types/gmud'
import { COLUNAS_DO_QUADRO, idDaColuna, quantasNaColuna } from './colunasDoQuadro'
import { QUADRO } from './textosDoQuadro'

/**
 * Os sete atalhos para as colunas, visíveis e nomeados.
 *
 * Sete colunas lado a lado significam rolagem lateral, e rolagem lateral por teclado é Tab através
 * de todos os cartões de todas as colunas anteriores. Esta barra é a saída — e ela é um `<nav>` de
 * botões comuns, não um modo a descobrir: o público são fiscais e servidores leigos em TI, para
 * quem atalho invisível não existe.
 *
 * A contagem entra no rótulo porque ela é metade da resposta: saber que a etapa 3 tem quatro
 * solicitações paradas muitas vezes já basta, sem chegar até lá.
 *
 * @param props o agrupamento por estado, de onde saem as contagens
 * @returns a barra de salto
 */
export function BarraDeSalto({ porStatus }: { porStatus: Map<StatusGmud, ResumoGmud[]> }) {
  function saltarPara(numero: number) {
    const alvo = document.getElementById(idDaColuna(numero))
    alvo?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    // O foco vai junto com a rolagem: mover só a rolagem deixa quem usa teclado exatamente onde
    // estava, e o próximo Tab volta para a barra em vez de entrar na coluna.
    alvo?.focus({ preventScroll: true })
  }

  // Uma faixa só, que rola de lado, em vez de sete botões com "· 0 solicitações" por extenso que
  // quebravam em duas linhas e empurravam o quadro para baixo. A contagem virou selo — cheio quando
  // há solicitação parada na etapa, apagado quando não há — e continua por extenso para o leitor de
  // tela. Numerada porque É uma sequência: são as sete etapas do ANEXO XXI, nesta ordem.
  return (
    <nav aria-label={QUADRO.irPara}>
      <ol className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-cartao">
        {COLUNAS_DO_QUADRO.map((coluna) => {
          const quantas = quantasNaColuna(porStatus, coluna)
          return (
            <li key={coluna.etapa.numero} className="shrink-0">
              <button
                type="button"
                onClick={() => saltarPara(coluna.etapa.numero)}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm text-slate-700',
                  'transition-colors hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                <span
                  aria-hidden="true"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600"
                >
                  {coluna.etapa.numero}
                </span>
                {coluna.etapa.titulo}
                <span
                  className={cn(
                    'min-w-[1.25rem] rounded-full px-1.5 text-center text-xs font-semibold tabular-nums',
                    quantas > 0 ? 'bg-niteroi-700 text-white' : 'bg-slate-100 text-slate-500',
                  )}
                >
                  <span aria-hidden="true">{quantas}</span>
                  <span className="sr-only">, {QUADRO.contagem(quantas)}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
